import { Spacer } from '@kizen/kds/Spacer';
import { HorizontalTile, Panel } from '@kizen/kds/Tile';
import { HeaderText, Typography } from '@kizen/kds/Typography';
import Switch from '__components/Kizen/Switch';
import useModal from '__components/Modals/useModal';
import { Button } from '@kizen/kds/Button';
import { MarketplacePlugin } from 'pages/Marketplace/types';
import { useTranslation } from 'react-i18next';
import { Icon } from '@kizen/kds/Icon';
import {
  PanelFooter,
  PanelSizer,
  Thumbnail,
  ThumbnailSizer,
} from 'pages/Marketplace/styles';
import { Link, useHistory } from 'react-router-dom';
import { useCallback } from 'react';
import { DEFAULT_PATHS } from 'routes/paths';
import { ConfirmationModal } from './ConfirmationModal';
import { TFunction } from 'i18next';
import { useMarketplacePermissions } from '__pages/Marketplace/permissions';
import {
  BusinessPluginApp,
  InstalledPlugin,
} from 'ts-components/Plugins/types';

interface TileProps {
  onChangeEnabled?: (
    pluginApiName: string,
    enabled: boolean,
    plugin: MarketplacePlugin
  ) => Promise<unknown>;
  onChangeConfig?: (
    pluginApiName: string,
    config: Record<string, any>
  ) => Promise<unknown>;
  plugin: MarketplacePlugin;
  installation?: InstalledPlugin;
  overrideCheckedState?: boolean;
  hasBusinessPluginApp: boolean;
  qa?: Record<string, any>;
  businessPluginApp?: BusinessPluginApp;
}

type IconConfig = {
  isUpgradable: boolean;
  isError: boolean;
  errorCount?: number;
  canViewPlugins?: boolean;
};

export const getFilePath = (path: string) => {
  if (path.startsWith('/') || path.startsWith('data:')) {
    return path;
  }

  return `${import.meta.env.VITE_API_BASE_PATH}/files/${path}/download`;
};

const getNotificationIcon = (config: IconConfig) => {
  const { isUpgradable, isError } = config;

  if (isUpgradable || isError) {
    return 'message-warning-triangle';
  }

  return 'message-info-circle';
};

const getNotificationColor = (config: IconConfig) => {
  const { isUpgradable, isError } = config;

  if (isUpgradable || isError) {
    return 'button-warning-default';
  }

  return 'font-primary';
};

const getNotificationText = (
  config: IconConfig,
  t: TFunction,
  { version, installation }: { version: string; installation?: InstalledPlugin }
) => {
  const { isUpgradable, isError, errorCount } = config;

  if (isUpgradable) {
    return t('Upgrade available from {{version}} to {{newVersion}}', {
      version,
      newVersion: installation?.upgradeable,
    });
  }

  if (isError) {
    return t(
      'This app has encountered {{errorCount}} {{noun}}. Click to view details.',
      {
        errorCount: errorCount || t('one or more'),
        noun: errorCount === 1 ? t('error') : t('errors'),
      }
    );
  }

  return t('Installed Version: {{version}}', {
    version,
  });
};

const getNotificationLink = (config: IconConfig, pluginApiName: string) => {
  const { isError } = config;

  if (isError) {
    return `${DEFAULT_PATHS.MARKETPLACE}/${pluginApiName}/auth`;
  }

  if (!config.canViewPlugins) {
    return `${DEFAULT_PATHS.MARKETPLACE}/${pluginApiName}/details`;
  }

  return `${DEFAULT_PATHS.MARKETPLACE}/${pluginApiName}/version`;
};

export const Tile = (props: TileProps) => {
  const {
    onChangeEnabled,
    plugin,
    installation,
    overrideCheckedState,
    hasBusinessPluginApp,
    qa,
    businessPluginApp,
  } = props;

  const {
    showEditConfigurationButton,
    isReadOnly,
    showManageIntegrationSecretsButton,
    canViewPlugins,
  } = useMarketplacePermissions(plugin);

  const isError =
    hasBusinessPluginApp && (businessPluginApp?.error_count || 0) > 0;

  const { t } = useTranslation();
  const history = useHistory();

  const isEnabled = Boolean(installation);

  const editClickHandler = useCallback(
    (e: any) => {
      e.preventDefault();
      if (typeof plugin.base_config?.editConfigurationOverride === 'string') {
        return history.push(plugin.base_config.editConfigurationOverride);
      } else {
        return history.push(`/marketplace/${plugin.api_name}/configure`);
      }
    },
    [history, plugin]
  );

  const [confirmationModalProps, , confirmationModal] = useModal({
    handleSubmit: async () => {
      onChangeEnabled?.(plugin.api_name, false, plugin);
    },
  });

  const isUpgradable = Boolean(installation?.upgradeable);
  const version = installation?.version;

  const totalNotifications =
    (businessPluginApp?.error_count || 0) +
    (businessPluginApp?.notification_count || 0);

  return (
    <>
      <HorizontalTile
        qa={qa}
        onClick={
          plugin.base_config?.disableDetailView
            ? undefined
            : (e) => {
                // only handle click if it wasn't handled by an existing click event
                if (!e.defaultPrevented) {
                  history.push(`/marketplace/${plugin.api_name}/details`);
                }
              }
        }
        center={
          <div className="w-full relative">
            <Panel expand>
              <PanelSizer>
                <div className="flex">
                  {plugin.thumbnail ? (
                    <div>
                      <ThumbnailSizer>
                        {plugin.base_config?.disableDetailView ? (
                          <Thumbnail src={getFilePath(plugin.thumbnail)} />
                        ) : (
                          <Link
                            to={`${DEFAULT_PATHS.MARKETPLACE}/${plugin.api_name}/details`}
                          >
                            <Thumbnail src={getFilePath(plugin.thumbnail)} />
                          </Link>
                        )}
                      </ThumbnailSizer>
                      <Spacer size={20} mode="horizontal" />
                    </div>
                  ) : null}
                </div>
                <div className="absolute top-0 right-0">
                  {version ? (
                    <Link
                      to={getNotificationLink(
                        {
                          isUpgradable,
                          isError,
                          errorCount: businessPluginApp?.error_count,
                          canViewPlugins,
                        },
                        plugin.api_name
                      )}
                    >
                      <Icon
                        icon={getNotificationIcon({ isUpgradable, isError })}
                        color={getNotificationColor({ isUpgradable, isError })}
                        tooltipDescriptionOverride={getNotificationText(
                          {
                            isUpgradable,
                            isError,
                            errorCount: businessPluginApp?.error_count,
                          },
                          t,
                          { version, installation }
                        )}
                        tooltipEnabled
                        badge={totalNotifications > 0}
                        badgeColor={
                          businessPluginApp?.error_count
                            ? 'error'
                            : 'information'
                        }
                      />
                    </Link>
                  ) : plugin.base_config?.disableDetailView ? (
                    <Icon
                      icon={
                        isUpgradable
                          ? 'message-warning-triangle'
                          : 'message-info-circle'
                      }
                      color={
                        isUpgradable ? 'button-warning-default' : 'font-primary'
                      }
                      tooltipDescriptionOverride={t(
                        'This app is automatically installed and cannot be disabled'
                      )}
                      tooltipEnabled
                    />
                  ) : hasBusinessPluginApp ? (
                    <Link
                      to={`${DEFAULT_PATHS.MARKETPLACE}/${plugin.api_name}/details`}
                    >
                      <Icon
                        icon="message-info-circle"
                        color="font-primary"
                        tooltipDescriptionOverride={t('App not installed')}
                        tooltipEnabled
                      />
                    </Link>
                  ) : null}
                </div>

                <div className="flex items-center justify-start kds">
                  {!isReadOnly ? (
                    <>
                      <div onClick={(e) => e.stopPropagation()}>
                        <Switch
                          checked={
                            plugin.base_config?.forceEnable
                              ? true
                              : overrideCheckedState ?? isEnabled
                          }
                          onChange={(ev) => {
                            if (plugin.base_config?.forceEnable) {
                              return;
                            }

                            if (ev.target.checked) {
                              return onChangeEnabled?.(
                                plugin.api_name,
                                true,
                                plugin
                              );
                            } else {
                              return confirmationModal.show();
                            }
                          }}
                          removeMargin
                          disabled={typeof overrideCheckedState === 'boolean'}
                          data-qa="toggle-plugin"
                        />
                      </div>
                      <Spacer size={10} mode="vertical" />
                    </>
                  ) : null}
                  <HeaderText
                    size="xl"
                    weight="semibold"
                    className="w-full pr-12"
                    truncate
                  >
                    {plugin.name}
                  </HeaderText>
                </div>
                {plugin.description ? (
                  <div className="mb-auto mt-4">
                    <Typography className="line-clamp-2 pb-2">
                      {plugin.description}
                    </Typography>
                  </div>
                ) : null}
                <PanelFooter>
                  <div className="flex w-full">
                    {(hasBusinessPluginApp ||
                      plugin.base_config?.editConfigurationOverride) &&
                    showEditConfigurationButton &&
                    plugin.base_config?.user_configurable !== false ? (
                      <div className="mr-auto">
                        <Button
                          onClick={editClickHandler}
                          variant="text"
                          size="xs"
                          color="secondary"
                        >
                          {t('Edit Configuration')}
                        </Button>
                      </div>
                    ) : null}
                    {showManageIntegrationSecretsButton(businessPluginApp) ? (
                      <Link
                        className="ml-auto"
                        to="/settings/manage-connections/integration-secrets"
                      >
                        <Button variant="text" size="xs" color="secondary">
                          {t('Manage Secrets')}
                        </Button>
                      </Link>
                    ) : null}
                  </div>
                </PanelFooter>
              </PanelSizer>
            </Panel>
          </div>
        }
      />
      <ConfirmationModal
        confirmationModalProps={confirmationModalProps}
        installedPlugin={plugin}
      />
    </>
  );
};
